/**
 * Custom hook for managing pagination state.
 *
 * Eliminates the repeated pattern of:
 *   const [currentPage, setCurrentPage] = useState(1);
 *   const [totalPages, setTotalPages] = useState(1);
 *   const [totalCount, setTotalCount] = useState(0);
 *   const pageSize = 12;
 *   const handlePageChange = (newPage: number) => { ... }
 *
 * Usage:
 *   const pagination = usePagination({ pageSize: 20 });
 *
 *   // After API call:
 *   pagination.setFromResponse(response.data.count);
 *
 *   // In pagination component:
 *   <Pagination
 *     currentPage={pagination.currentPage}
 *     totalPages={pagination.totalPages}
 *     onPageChange={pagination.goToPage}
 *   />
 */

import { useState, useCallback, useMemo } from 'react';

export interface PaginationOptions {
  /** Items per page (default: 20) */
  pageSize?: number;
  /** Initial page (default: 1) */
  initialPage?: number;
  /** Scroll to top on page change (default: true) */
  scrollToTop?: boolean;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
}

export interface PaginationResult {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;

  /** Go to a specific page */
  goToPage: (page: number) => void;
  /** Go to next page */
  nextPage: () => void;
  /** Go to previous page */
  prevPage: () => void;
  /** Reset to first page */
  reset: () => void;

  /** Update pagination from API response count */
  setFromResponse: (count: number) => void;
  /** Manual setters */
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

  /** Helper: is on first page */
  isFirstPage: boolean;
  /** Helper: is on last page */
  isLastPage: boolean;
  /** Helper: has multiple pages */
  hasMultiplePages: boolean;

  /** Calculate start/end indices for display */
  startIndex: number;
  endIndex: number;

  /** Update page size dynamically */
  setPageSize: (size: number) => void;
}

/**
 * Manages pagination state (current page, total pages, navigation) for list views.
 * Call `setFromResponse(count)` after an API call to synchronize total count.
 *
 * @param options - Page size, initial page, scroll behavior, and change callback
 */
export function usePagination(options: PaginationOptions = {}): PaginationResult {
  const {
    pageSize = 20,
    initialPage = 1,
    scrollToTop = true,
    onPageChange,
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [dynamicPageSize, setDynamicPageSize] = useState(pageSize);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      onPageChange?.(page);
    }
  }, [totalPages, scrollToTop, onPageChange]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const reset = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const setFromResponse = useCallback((count: number) => {
    setTotalCount(count);
    setTotalPages(Math.ceil(count / dynamicPageSize) || 1);
  }, [dynamicPageSize]);

  const handleSetPageSize = useCallback((size: number) => {
    setDynamicPageSize(size);
    setCurrentPage(1);
    // Recalculate total pages with new size
    if (totalCount > 0) {
      setTotalPages(Math.ceil(totalCount / size) || 1);
    }
  }, [totalCount]);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;
  const hasMultiplePages = totalPages > 1;

  const startIndex = useMemo(() => (currentPage - 1) * dynamicPageSize + 1, [currentPage, dynamicPageSize]);
  const endIndex = useMemo(() => Math.min(currentPage * dynamicPageSize, totalCount), [currentPage, dynamicPageSize, totalCount]);

  return {
    currentPage,
    totalPages,
    totalCount,
    pageSize: dynamicPageSize,
    goToPage,
    nextPage,
    prevPage,
    reset,
    setFromResponse,
    setTotalCount,
    setTotalPages,
    setCurrentPage,
    isFirstPage,
    isLastPage,
    hasMultiplePages,
    startIndex,
    endIndex,
    setPageSize: handleSetPageSize,
  };
}

/**
 * Generates a windowed array of page numbers for pagination UI display.
 *
 * @param currentPage - The currently active page (1-indexed)
 * @param totalPages - Total number of pages
 * @param maxVisible - Maximum number of page buttons to show (default: 5)
 * @returns Array of page numbers centered around the current page
 */
export function getPageNumbers(currentPage: number, totalPages: number, maxVisible = 5): number[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const half = Math.floor(maxVisible / 2);
  let start = currentPage - half;
  let end = currentPage + half;

  if (start < 1) {
    start = 1;
    end = maxVisible;
  } else if (end > totalPages) {
    end = totalPages;
    start = totalPages - maxVisible + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default usePagination;
